import React, { useState } from "react"
import {
  InfoCircleOutlined,
  AreaChartOutlined,
  TableOutlined,
  AimOutlined,
  ReloadOutlined,
} from "@ant-design/icons"
import {
  theme as themeConfig,
  Segmented,
  Popover,
  Alert,
  Table,
  Tooltip,
  Button,
  Spin,
  Tag,
} from "antd"
import { DownloadOutlined } from "@ant-design/icons"

import { IMG_GRAPH } from "../../../../../../data/assets"

import CardContainer from "../../../../components/CardContainer"

import PieChartWithLegend from "./PieChartWithLegend"
import LineChartWithLegend from "./LineChartWithLegend"
import BarChartWithLegend from "./BarChartWithLegend"
import HeatMap from "./HeatMap"
import FunnelChart from "./FunnelChart"
import RefreshFilters from "../RefreshFilters"

import "../../index.scss"

const GraphTableCard = ({ graph, loadError, loading, onRefresh }) => {
  const { token } = themeConfig.useToken()
  const [viewMode, setViewMode] = useState("graph")
  const [drawerOpen, setDrawerOpen] = useState(false)

  if (!graph) return null

  const isEmpty = graph?.graphData?.length === 0

  const renderGraphByType = graph => {
    switch (graph.graphType) {
      case "pie":
        return <PieChartWithLegend graph={graph} />
      case "funnel":
        return <FunnelChart graph={graph} />
      case "line":
        return <LineChartWithLegend graph={graph} />
      case "bar":
        return <BarChartWithLegend graph={graph} />
      case "heatmap":
        return <HeatMap graph={graph} />
      default:
        return null
    }
  }

  const handleDownload = () => {
    const columns = graph?.secondaryViewDetails?.tableData?.columns || []
    const dataSource = graph?.secondaryViewDetails?.tableData?.dataSource || []
    if (!columns.length || !dataSource.length) return

    // Header row
    const header = columns
      .map(col => `"${col.title || col.dataIndex}"`)
      .join(",")
    // Data rows
    const rows = dataSource.map(row =>
      columns.map(col => `"${row[col.dataIndex] ?? ""}"`).join(",")
    )
    const csvContent = [header, ...rows].join("\r\n")

    // Download
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${graph?.title}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleRefresh = (refreshData, callback) => {
    const dataWithId = {
      ...refreshData,
      id: graph.id,
    }

    if (onRefresh) {
      onRefresh(dataWithId, callback)
    }
  }

  const viewDetails =
    viewMode === "table" && graph?.secondaryViewDetails
      ? graph.secondaryViewDetails
      : graph || {}

  const getRowClassName = record => {
    if (!record?.attributes?.state) return ""

    switch (record.attributes.state.toLowerCase()) {
      case "success":
        return "status-success-row"
      case "warning":
        return "status-warning-row"
      case "error":
        return "status-error-row"
      default:
        return ""
    }
  }

  const processColumns = (columns, dataSource) => {
    if (!columns || !columns.length) return []

    return columns.map(col => {
      const newCol = {
        ...col,
        width: "auto",
        render: (value, record) => {
          if (value && typeof value === "object" && "data" in value) {
            const stateClass = value?.attributes?.state
              ? `cell-state-${value?.attributes?.state?.toLowerCase()}`
              : ""

            return (
              <span className={stateClass}>
                {value.data !== undefined ? value.data : "-"}
              </span>
            )
          }
          return value
        },
      }

      if (col.rowSpan && col.rowSpan > 1) {
        const spanSize = col.rowSpan

        newCol.onCell = (_, index) => {
          if (index % spanSize === 0) {
            return { rowSpan: spanSize }
          }
          return { rowSpan: 0 }
        }
      }

      return newCol
    })
  }

  const renderTableView = () => {
    if (graph.secondaryViewDetails?.componentType === "table") {
      return (
        <div
          style={{
            paddingLeft: token.paddingXS,
            paddingRight: token.paddingXS,
            height: "100%",
          }}
        >
          <Table
            columns={processColumns(
              graph?.secondaryViewDetails?.tableData?.columns
            )}
            dataSource={graph?.secondaryViewDetails?.tableData?.dataSource}
            pagination={false}
            bordered
            size="small"
            rowClassName={getRowClassName}
            scroll={{ y: 305 }}
          />
        </div>
      )
    }
    return null
  }

  return (
    <>
      <CardContainer
        style={{
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadowTertiary,
          minHeight: 435,
          marginBottom: 0,
          position: "relative",
          overflow: "hidden",
        }}
        empty_condition={isEmpty && !drawerOpen}
        load_error={loadError}
        empty_show_message_props={{
          img_src: IMG_GRAPH,
          title: "Empty Data",
          message: "Data not found for this filter",
        }}
        error_show_message_props={{
          title: "Error",
          message: "Unable to fetch data",
        }}
        header={
          <div
            style={{
              color: token.colorText,
              paddingTop: token.paddingSM,
              paddingBottom: token.paddingSM,
              fontWeight: token.fontWeightStrong,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span>{viewDetails?.title}</span>
              {viewDetails?.tooltip?.data?.some(d => d?.description) && (
                <Popover
                  trigger="hover"
                  placement="bottomRight"
                  styles={{
                    body: { padding: 0, minWidth: 200 },
                    root: { maxWidth: 280 },
                  }}
                  content={
                    <Alert
                      type="info"
                      showIcon
                      style={{
                        padding: token.paddingXS,
                        fontSize: token.fontSize,
                      }}
                      message={
                        <div
                          style={{
                            fontSize: token.fontSize,
                            fontWeight: token.fontWeightStrong,
                          }}
                        >
                          {viewDetails?.tooltip?.title}
                        </div>
                      }
                      description={
                        <div
                          style={{
                            fontSize: token.fontSizeSM,
                            marginTop: token.marginXS,
                          }}
                        >
                          {viewDetails?.tooltip?.data?.map((tip, index) =>
                            tip?.description ? (
                              <div
                                key={index}
                                style={{ marginBottom: 2, lineHeight: 1.3 }}
                              >
                                {tip?.subtitle ? (
                                  <strong>{tip.subtitle}</strong>
                                ) : null}
                                {tip?.subtitle && tip?.description ? ": " : ""}
                                {tip?.description}
                              </div>
                            ) : null
                          )}
                        </div>
                      }
                    />
                  }
                >
                  <InfoCircleOutlined
                    style={{
                      color: token.colorTextDescription,
                      cursor: "pointer",
                      fontSize: token.fontSizeSM,
                    }}
                  />
                </Popover>
              )}
              {!loadError && !loading && viewDetails?.legend && (
                <Popover
                  trigger="hover"
                  placement="bottomRight"
                  styles={{
                    body: { padding: 0, minWidth: 520 },
                    root: { maxWidth: 600 },
                  }}
                  content={
                    <Alert
                      type="info"
                      showIcon
                      style={{
                        padding: token.paddingXS,
                        fontSize: token.fontSize,
                      }}
                      message={
                        <div
                          style={{
                            fontSize: token.fontSize,
                            fontWeight: token.fontWeightStrong,
                          }}
                        >
                          {viewDetails.legend.title}
                        </div>
                      }
                      description={
                        <div
                          style={{
                            fontSize: token.fontSizeSM,
                            marginTop: token.marginXS,
                            maxHeight: 300,
                            overflow: "auto",
                            minHeight: 120,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                              gap: 8,
                            }}
                          >
                            {viewDetails.legend.data?.map((criteria, index) => (
                              <div
                                key={index}
                                style={{
                                  flex: "1 1 200px",
                                  padding: token.paddingXS,
                                  border: `1px solid ${token.colorBorderSecondary}`,
                                  borderRadius: token.borderRadiusLG,
                                  background: token.colorBgElevated,
                                }}
                              >
                                {criteria.subtitle && (
                                  <div
                                    style={{
                                      fontWeight: token.fontWeightStrong,
                                      marginBottom: token.marginXXS,
                                      fontSize: token.fontSizeSM,
                                    }}
                                  >
                                    {criteria.subtitle}
                                  </div>
                                )}
                                {criteria.successCriteria && (
                                  <div
                                    style={{
                                      marginBottom: token.marginXXS,
                                      fontSize: token.fontSizeSM,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontWeight: token.fontWeightStrong,
                                      }}
                                    >
                                      Success Criteria:
                                    </span>{" "}
                                    {criteria.successCriteria}
                                  </div>
                                )}
                                {criteria.threshold && (
                                  <div style={{ marginTop: token.marginXXS }}>
                                    <div
                                      style={{
                                        fontWeight: token.fontWeightStrong,
                                        marginBottom: token.marginXXS,
                                        fontSize: token.fontSizeSM,
                                      }}
                                    >
                                      Thresholds:
                                    </div>
                                    <ul
                                      style={{
                                        margin: 0,
                                        paddingLeft: 0,
                                        listStyleType: "none",
                                      }}
                                    >
                                      {criteria?.threshold?.success && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: token.marginXXS,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#52c41a",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>
                                            {criteria.threshold.success}
                                          </span>
                                        </li>
                                      )}
                                      {criteria.threshold.warn && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: token.marginXXS,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#faad14",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>{criteria.threshold.warn}</span>
                                        </li>
                                      )}
                                      {criteria.threshold.error && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                            marginBottom: token.marginXXS,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#ff4d4f",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>
                                            {criteria.threshold.error}
                                          </span>
                                        </li>
                                      )}
                                      {criteria.threshold.normal && (
                                        <li
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                          }}
                                        >
                                          <span
                                            style={{
                                              width: 8,
                                              height: 8,
                                              borderRadius: "50%",
                                              backgroundColor: "#d9d9d9",
                                              display: "inline-block",
                                            }}
                                          ></span>
                                          <span>
                                            Normal: {criteria.threshold.normal}
                                          </span>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      }
                    />
                  }
                >
                  <AimOutlined
                    style={{
                      color: token.colorTextDescription,
                      cursor: "pointer",
                      fontSize: token.fontSizeSM,
                    }}
                  />
                </Popover>
              )}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: token.marginSM,
                marginBottom: 8,
              }}
            >
              {graph?.updated && (
                <Tag
                  style={{
                    fontSize: token.fontSizeXS,
                    fontWeight: token.fontWeightStrong,
                    margin: 0,
                    backgroundColor: token.colorPrimary,
                    color: token.colorTextLightSolid,
                  }}
                >
                  Updated
                </Tag>
              )}
              {graph?.refreshComponent && (
                <Tooltip title="Refresh Data">
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={() => setDrawerOpen(true)}
                  />
                </Tooltip>
              )}
              {graph.hasSecondaryView &&
                graph?.secondaryViewDetails?.tableData?.dataSource?.length >
                  0 && (
                  <>
                    <Segmented
                      size="small"
                      value={viewMode}
                      onChange={setViewMode}
                      options={[
                        {
                          label: (
                            <Tooltip title="Chart View">
                              <AreaChartOutlined />
                            </Tooltip>
                          ),
                          value: "graph",
                        },
                        {
                          label: (
                            <Tooltip title="Table View">
                              <TableOutlined />
                            </Tooltip>
                          ),
                          value: "table",
                        },
                      ]}
                    />
                    <Tooltip title="Download File">
                      <Button
                        icon={<DownloadOutlined />}
                        size="small"
                        onClick={handleDownload}
                        disabled={
                          !graph?.secondaryViewDetails?.tableData?.columns
                            ?.length ||
                          !graph?.secondaryViewDetails?.tableData?.dataSource
                            ?.length
                        }
                      />
                    </Tooltip>
                  </>
                )}
            </div>
          </div>
        }
      >
        {/* Main Content */}
        <div style={{ position: "relative", height: "100%" }}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: 350,
              }}
            >
              <Spin tip="Loading data..." />
            </div>
          ) : viewMode === "graph" ? (
            renderGraphByType(graph)
          ) : (
            renderTableView()
          )}

          {drawerOpen && (
            <RefreshFilters
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              onRefresh={handleRefresh}
              loading={loading}
              item={graph}
              title="Refresh Chart Data"
              styles={{
                height: "100%",
              }}
            />
          )}

          {drawerOpen && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.25)",
                zIndex: 1000,
                cursor: "pointer",
              }}
              onClick={() => setDrawerOpen(false)}
            />
          )}
        </div>
      </CardContainer>
    </>
  )
}

export default GraphTableCard
